
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import OrderHeader from './order-details/OrderHeader';
import AddressDetails from './AddressDetails';
import BuyerDetails from './BuyerDetails';
import OrderActions from './OrderActions';
import ShippingDetailsDisplay from './ShippingDetails';
import { Order } from './order-types';
import { Badge } from '@/components/ui/badge';
import { AlertCircle } from 'lucide-react';

interface OrderCardProps {
  order: Order;
  isUpdating: boolean;
  onViewListing: (listingId: number) => void;
  onViewBuyerProfile: (buyerId: string) => void;
  onContactBuyer: (order: Order) => void;
  onUpdateStatus: (orderId: string, newStatus: string) => void;
  onCancelOrder?: (orderId: string) => void;
  onConfirmCancellation?: (orderId: string) => void;
  onRejectCancellation?: (orderId: string) => void;
}

const OrderCard = ({
  order,
  isUpdating,
  onViewListing,
  onViewBuyerProfile,
  onContactBuyer,
  onUpdateStatus,
  onCancelOrder,
  onConfirmCancellation,
  onRejectCancellation
}: OrderCardProps) => {
  const needsCancellationConfirmation = order.status === 'cancelled' && order.requires_confirmation;

 

  return (
    <Card key={order.id} className="overflow-hidden border-gray-200 hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-4">
        <div className="flex flex-col space-y-4">
          {/* Product header with image and info */}
          <OrderHeader 
            order={order} 
          />
          
          {/* Cancellation request indicator */}
          {needsCancellationConfirmation && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-md">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-amber-800">Kërkesë për anulim</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Blerësi ka kërkuar anulimin e kësaj porosie.
                  </p>
                  {order.cancellation_reason && (
                    <div className="mt-2 text-sm">
                      <p className="font-medium text-amber-800">Arsyeja:</p>
                      <p className="text-amber-700 bg-amber-100 p-2 rounded mt-1">{order.cancellation_reason}</p>
                    </div>
                  )}
                  
                  <div className="flex space-x-2 mt-3">
                    <button
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                      onClick={() => onConfirmCancellation?.(order.id)}
                      disabled={isUpdating}
                    >
                      Konfirmo Anulimin
                    </button>
                    <button
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                      onClick={() => onRejectCancellation?.(order.id)}
                      disabled={isUpdating}
                    >
                      Refuzo Anulimin
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Show cancellation reason if order is cancelled */}
          {order.status === 'cancelled' && !order.requires_confirmation && order.cancellation_reason && (
            <div className="bg-red-50 border border-red-200 p-3 rounded-md">
              <h4 className="font-medium text-red-800 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                Arsyeja e anulimit:
              </h4>
              <p className="text-sm text-red-700 mt-1">{order.cancellation_reason}</p>
            </div>
          )}
          
          {/* Shipping details if available */}
          {order.status === 'shipped' && (
            <ShippingDetailsDisplay shippingDetails={order.shipping_details} />
          )}
          
          {/* Delivery address information */}
          <AddressDetails addressJson={order.delivery_address} />
          
          {/* Buyer information */}
          <BuyerDetails 
            buyerProfile={order.buyer_profile || null} 
            onViewProfile={onViewBuyerProfile}
            onContactBuyer={() => onContactBuyer(order)}
          />
          
          {/* Action buttons */}
          <OrderActions 
            orderId={order.id}
            status={order.status}
            listingId={order.listings?.id}
            isUpdating={isUpdating}
            onViewListing={onViewListing}
            onUpdateStatus={onUpdateStatus}
            onCancelOrder={onCancelOrder}
            needsCancellationConfirmation={needsCancellationConfirmation}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderCard;
